import React, {useState, useEffect, useRef} from 'react'
import {ModulePanel, ModuleHeader, ModuleBody, ModuleFooter} from 'containers/purchase/modules/module_panel'
import { useTranslation } from 'react-i18next'
import {Row, Col, Nav, NavItem, NavLink, TabContent, TabPane} from 'reactstrap'
import styled from 'styled-components'
import inicial from 'img/iryo/inicial.png'
import inicialSuperior from 'img/iryo/inicial-superior.png'
import infinita from 'img/iryo/infinita.png'
import infinitaBistro from 'img/iryo/infinita-bistro.png'
import train from 'img/iryo/iryo-train'
import time from 'i18n/time'
import {currency} from 'i18n/config'
import {get} from 'lodash'
import cn from 'classnames'
import {scrollTo, scrollIntoView} from 'scroll-js'
import ProductDetails from './product_details'
import {isMobile} from 'react-device-detect'
import { is } from 'date-fns/locale'
import {colorClasses} from './classes'

const Cell = styled(Col)`
  padding: 0.25em;
`;

const CellContent = styled.div`
  border-top-left-radius: 0.3rem;
  border-top-right-radius: 0.3rem;
  font-family: emeric,sans-serif;
  font-weight: 400;
  height: 95px;
  font-size: .8rem;
  opacity: 1;
  transition: all .5 cubic-bezier(.25,.8,.25,1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f6f6f6;
  color: #000;
  border-bottom: 4px solid #f2eef0;
  font-size: 1.25rem;
  color: #584c53;
  font-weight: 600;
`;

const CellClickable = styled(CellContent)`
  cursor: ${props => props.active ? 'pointer' : 'auto'};

  &:hover {
    background-color: #fff;
    box-shadow: 0 11px 44px #00000036;
  }
`;

const Header = styled(Cell)`
  background: transparent;
  border-bottom-color: #1d1d1b;
`;

const Inicial = styled(CellClickable)`
  border-bottom-color: #dd0915;
`;

const InicialSuperior = styled(CellClickable)`
  border-bottom-color: #dd0915;
`;

const Infinita = styled(CellClickable)`
  border-bottom-color: #1d1d1b;
`;

const InfinitaBistro = styled(CellClickable)`
  border-bottom-color: #1d1d1b;
`;

const ProductPill = styled.div.attrs({
  className: 'd-flex justify-content-between bg-white m-2 p-2 fw-bold border vertical-align-middle shadow'
})`
  border-color: ${props => props.color} !important;
  border-bottom-width: 5px !important;
`;

const NoRoutesAlert = () => {
  const { t, i18n } = useTranslation()

  return (
    <div className="alert alert-light my-3">
      {i18n.t('iryo_train_module.no_routes_alert')}
    </div>
  )
}

const ProductBreadcrumb = ({active, type, travel, products}) => {
  const {i18n} = useTranslation()

  return (
    <div className={cn("p-2 rounded d-flex justify-content-center align-items-center", active ? 'bg-active' : '')}>
      <div className="me-2" style={{transform: type === 'inbound' ? 'scaleX(-1)' : ''}}>
        <img className="align-self-center" src={train}/>
      </div>
      <div className="text-left">
        <div style={{fontSize: '0.8em'}}>
          <span className="text-uppercase text-muted me-2">
            {type === 'outbound'
              ? i18n.t('iryo_train_module.one_way')
              : i18n.t('iryo_train_module.round_trip')
            }:
          </span>
          {travel &&
            <span style={{fontSize: '1.5em'}} className="fw-bold">
              {time.parseZone(travel.route.legs[0].departure_station.departure_timestamp).format('LL')}
            </span>
          }
        </div>
        {travel ? (
          <div className="d-flex justify-content-start">
            <div className="pe-3">
              <span className="fw-bold">{i18n.t('iryo_train_module.time')}:</span>{' '}
              {time.parseZone(travel.route.legs[0].departure_station.departure_timestamp).format('LT')}
            </div>
            <div className="px-3">
              <span className="fw-bold">
                {products.find((p) => p.code === travel.product.items[0].product_code).name}:
              </span>{' '}
              {currency(travel.product.price)}
            </div>
          </div>
        ) : (
          <div>
            {type === 'inbound' && !active
              ? '-'
              : <strong>{i18n.t('iryo_train_module.select_modality')}</strong>}
          </div>
        )}
      </div>
    </div>
  )
}

const IryoTrainModule = (props) => {
  const {config, machine, status, resSize} = props
  const [current, send] = machine
  const {route_values, availability, outbound, inbound, activeTab} = current.context
  const { t, i18n } = useTranslation()
  const [scrollReady, setScrollReady] = useState(true)
  const outboundRoutes = get(availability, 'offer.travels[0].routes')
  const inboundRoutes = get(availability, 'offer.travels[1].routes')
  const products = get(availability, 'products', [])
  const features = get(availability, 'features', [])
  const specialRequests = get(availability, 'special_requests', [])
  const allergensList = get(availability, 'allergens', [])

  const [bistroModal, setBistroModal] = useState()
  const toggleBistroModal = () => setBistroModal(!bistroModal)

  const corporateCode = get(config, 'product.external_api_config.corporateCode')
  const luggagePenalty = get(config, 'product.external_api_config.luggagePenalty', 40)

  const setOutbound = (outbound) => {
    return send({type: 'OUTBOUND_CHANGE', outbound: outbound})
  }

  const setInbound = (inbound) => {
    return send({type: 'INBOUND_CHANGE', inbound: inbound})
  }

  const setActiveTab = (tab) => {
    return send({type: 'TAB_CHANGE', tab: tab})
  }

  const onConfirmProduct = () => {
    if (activeTab === 'outbound' && inboundRoutes) {
      const element = document.getElementById('iryo-train-module')
      scrollIntoView(element, document.body, { behavior: 'smooth' })
      setActiveTab('inbound')
    } else {
      send({ type: 'CONFIRMATION' })
    }
  }

  const onSelectMenuForAll = (addon, specialRequest, selected, setSelected, snackAddon) => {
    const passengers = availability.offer.passengers.filter((p) => p.type != 'IN')
    let newMenus = {}

    passengers.forEach((passenger, i) => {
      let passengerAddons = [
        {
          passengerId: passenger.id,
          tariffCode: addon.passenger_fares[i].tariff_code,
          specialRequests: [
            {
              type: specialRequest.type,
              code: specialRequest.code
            }
          ]
        }
      ]

      if (snackAddon) {
        passengerAddons.push({
          passengerId: passenger.id,
          tariffCode: snackAddon.passenger_fares[i].tariff_code,
        })
      }

      newMenus[passenger.id] = passengerAddons
    })

    setSelected({...selected, menus: newMenus})
  }

  const Departures = (props) => {
    const {type, selected, setSelected, departureArrival, onConfirmProduct, onSelectMenuForAll} = props

    const product = products.find((p) => p.code == (selected && selected.product.items[0].product_code))
    const availableFeatures = features && product && features.filter((f) => product.features.includes(f.code))

    let routes = props.routes
    if (type == 'inbound') {
      const limitTime = time.parseZone(departureArrival).add(1, 'hours')
      routes = routes.filter((r) => time.parseZone(r.legs[0].departure_station.departure_timestamp) >= limitTime)
    }

    if (!routes || routes.length <= 0) {
      return <NoRoutesAlert/>
    }

    return (
      <div>
        <Row className="d-none d-md-flex text-center">
          <Header style={{flexBasis: '17%'}}>
          </Header>
          <Header>
            <img width="180px" src={inicial}/>
          </Header>
          <Header>
            <img  width="180px" src={inicialSuperior}/>
          </Header>
          <Header>
            <img width="180px" src={infinita}/>
          </Header>
          <Header>
            <img width="180px" src={infinitaBistro}/>
          </Header>
        </Row>
        {routes.map((r) => {
          const departureTime = time.parseZone(r.legs[0].departure_station.departure_timestamp)
          const arrivalTime = time.parseZone(r.legs[0].arrival_station.arrival_timestamp)
          const interval = time.duration(arrivalTime.diff(departureTime, 'minutes'), 'minutes')
          const inicialProduct = r.bundles.find((b) => b.items[0].product_code == 'PT_FLEX2')
          const inicialSuperiorProduct = r.bundles.find((b) => b.items[0].product_code == 'PS_FLEX2')
          const infinitaProduct = r.bundles.find((b) => b.items[0].product_code == 'PS_CFFLEX2')
          const infinitaBistroProduct = r.bundles.find((b) => b.items[0].product_code == 'PI_GRFLEX2')
          const serviceName = r.legs[0].service_name

          const productIsActive = (product) => {
            if (!product) { return false }

            if (corporateCode && !product.ppc[0]) { return false }

            return true
          }

          const priceLabel = (product) => {
            if (!product) { return '-'}

            if (corporateCode && !product.ppc[0]) { return '-' }

            if (corporateCode) {
              return (
                <div className='d-flex d-md-block justify-content-center'>
                  <div className='d-flex d-md-block align-self-center text-center'>{currency(product.ppc[0])}</div>
                  <small className="text-danger"><strike>{currency(product.price)}</strike></small>
                </div>
              )
            } else {
              return (
                <div className='d-flex d-md-block d-md-block justify-content-center'>
                  <div className='d-flex d-md-block align-self-center text-center'>{currency(product.price)}</div>
                  {false &&
                    <small><i style={{color: '#939699', fontSize: '0.8em'}}>{i18n.t('iryo_train_module.fee_included', {amount: currency(product.fee)})}</i></small>
                  }
                </div>
              )
            }
          }

          const [menus, setMenus] = useState({})

          const serviceRowId = `service-${r.legs[0].service_name}`

          const onProductClick = (product) => {
            if (!product) { return '' }

            if (corporateCode && !product.ppc[0]) { return '' }

            const routeParams = {
              route: r,
              product: product
            }

            if (type == 'outbound') {
              setOutbound(routeParams)
            } else {
              setInbound(routeParams)
            }

            const element = document.getElementById('iryo-product-details')
            scrollIntoView(element, document.body, { behavior: 'smooth' })
          }

          const TrainTimes = () => {
            return (
              <>
                <div className="d-flex justify-content-center" style={{color: '#b28e99', fontSize: '1em', fontWeight: 'normal'}}>
                  {interval.format('h[h] mm[m]')}
                </div>
                <div className="d-flex justify-content-center align-items-center px-2 px-md-5 pb-2" style={{width: '100%', fontSize: '1.35em'}}>
                  <div className="fw-semibold">
                    {departureTime.format('LT')}
                  </div>
                  <div className="px-3">
                    <div className="d-none d-md-block" style={{borderBottom: '4px dashed #b28e99', flexGrow: '5'}}></div>
                    <div className="px-3" style={{color: '#b28e99'}}><i className="fa fa-train" aria-hidden="true"/></div>
                    <div className="d-none d-md-block" style={{borderBottom: '4px dashed #b28e99', flexGrow: '5'}}></div>
                  </div>
                  <div className="fw-semibold">
                    {arrivalTime.format('LT')}
                  </div>
                </div>
              </>
            )
          }

          console.log(colorClasses('PT_FLEX2'))

          if (isMobile) {
            return (
              <div className="bg-light rounded-3 mb-3 pt-2 pt-md-0">
                <Row>
                  <Col>
                    <TrainTimes/>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ProductPill color={colorClasses('PT_FLEX2')} onClick={() => onProductClick(inicialProduct)} active={productIsActive(inicialProduct)}>
                      <img width="150px" src={inicial}/>
                      <span className="d-flex" style={{fontSize: '1.15em'}}>{priceLabel(inicialProduct)}</span>
                    </ProductPill>
                    <ProductPill color={colorClasses('PS_FLEX2')} onClick={() => onProductClick(inicialSuperiorProduct)} active={productIsActive(inicialSuperiorProduct)}>
                      <img  width="150px" src={inicialSuperior}/>
                      <span className="d-flex" style={{fontSize: '1.15em'}}>{priceLabel(inicialSuperiorProduct)}</span>
                    </ProductPill>
                    <ProductPill color={colorClasses('PS_CFFLEX2')} onClick={() => onProductClick(infinitaProduct)} active={productIsActive(infinitaProduct)}>
                      <img width="150px" src={infinita}/>
                      <span className="d-flex" style={{fontSize: '1.15em'}}>{priceLabel(infinitaProduct)}</span>
                    </ProductPill>
                    <ProductPill color={colorClasses('PI_GRFLEX2')} onClick={() => onProductClick(infinitaBistroProduct)} active={productIsActive(infinitaBistroProduct)}>
                      <img width="150px" src={infinitaBistro}/>
                      <span className="d-flex" style={{fontSize: '1.15em'}}>{priceLabel(infinitaBistroProduct)}</span>
                    </ProductPill>
                  </Col>
                </Row>

                {scrollReady && product && (serviceName == selected.route.legs[0].service_name) &&
                  <ProductDetails
                    selected={selected}
                    setSelected={setSelected}
                    availability={availability}
                    specialRequests={specialRequests}
                    availableFeatures={availableFeatures}
                    bistroModal={bistroModal}
                    toggleBistroModal={toggleBistroModal}
                    product={product}
                    luggagePenalty={luggagePenalty}
                    priceLabel={priceLabel}
                    onConfirmProduct={onConfirmProduct}
                    setBistroModal={setBistroModal}
                    allergensList={allergensList}
                    onSelectMenuForAll={onSelectMenuForAll}
                    type={type}
                    setOutbound={setOutbound}
                    setInbound={setInbound}
                  />
                }
              </div>
            )
          }

          return (
            <React.Fragment key={r.id}>
              <Row id={serviceRowId} className="no-gutters text-center">
                <Cell style={{flexBasis: '17%'}} className="pl-0 pe-1">
                  <CellContent style={{background: 'white'}}>
                    <div className="d-flex justify-content-center" style={{color: '#b28e99', fontSize: '0.75em', fontWeight: 'normal'}}>
                      {interval.format('h[h] mm[m]')}
                    </div>
                    <div className="d-flex justify-content-center align-items-center px-5 pb-2" style={{width: '100%', fontSize: '0.95em'}}>
                      <div className="pe-3" style={{gap: '0.5rem'}}>
                        {departureTime.format('LT')}
                      </div>
                      <div style={{borderBottom: '4px dashed #b28e99', flexGrow: '5'}}></div>
                      <div className="px-3" style={{color: '#b28e99'}}><i className="fa fa-train" aria-hidden="true"/></div>
                      <div style={{borderBottom: '4px dashed #b28e99', flexGrow: '5'}}></div>
                      <div className="pl-3">
                        {arrivalTime.format('LT')}
                      </div>
                    </div>
                  </CellContent>
                </Cell>
                <Cell className="px-1" onClick={() => onProductClick(inicialProduct)}>
                  <Inicial active={productIsActive(inicialProduct)}>
                    {priceLabel(inicialProduct)}
                  </Inicial>
                </Cell>
                <Cell className="pl-1 pe-0" onClick={() => onProductClick(inicialSuperiorProduct)}>
                  <InicialSuperior active={productIsActive(inicialSuperiorProduct)}>
                    {priceLabel(inicialSuperiorProduct)}
                  </InicialSuperior>
                </Cell>
                <Cell className="px-1" onClick={() => onProductClick(infinitaProduct)}>
                  <Infinita active={productIsActive(infinitaProduct)}>
                    {priceLabel(infinitaProduct)}
                  </Infinita>
                </Cell>
                <Cell className="pl-1 pe-0" onClick={() => onProductClick(infinitaBistroProduct)}>
                  <InfinitaBistro active={productIsActive(infinitaBistroProduct)}>
                    {priceLabel(infinitaBistroProduct)}
                  </InfinitaBistro>
                </Cell>
              </Row>
              {scrollReady && product && (serviceName == selected.route.legs[0].service_name) &&
                <ProductDetails
                  selected={selected}
                  setSelected={setSelected}
                  availability={availability}
                  specialRequests={specialRequests}
                  availableFeatures={availableFeatures}
                  bistroModal={bistroModal}
                  toggleBistroModal={toggleBistroModal}
                  product={product}
                  luggagePenalty={luggagePenalty}
                  priceLabel={priceLabel}
                  onConfirmProduct={onConfirmProduct}
                  setBistroModal={setBistroModal}
                  allergensList={allergensList}
                  onSelectMenuForAll={onSelectMenuForAll}
                  type={type}
                  setOutbound={setOutbound}
                  setInbound={setInbound}
                />
              }
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  return (
    <ModulePanel id="iryo-train-module">
      <ModuleHeader status={status} onClick={() => status == 'completed' ? send('REOPEN_TRAINSTEP') : null}>
        <span>
          <i className="fa fa-clock me-2" aria-hidden="true"></i>
          {props.title}
        </span>
      </ModuleHeader>
      <ModuleBody status={status}>
        <Nav tabs fill>
          <NavItem
            style={{cursor: 'pointer'}}
            onClick={() => {
              if (inboundRoutes && activeTab != 'outbound') {
                setOutbound(null)
                setInbound(null)
                setActiveTab('outbound')
              }
            }}
          >
            <NavLink
              className={cn('p-1', {active: activeTab == 'outbound', disabled: activeTab != 'outbound'})}
            >
              <ProductBreadcrumb
                active={activeTab === 'outbound'}
                type="outbound"
                travel={outbound}
                products={products}
              />
            </NavLink>
          </NavItem>
          {inboundRoutes &&
            <NavItem>
              <NavLink
                className={cn('p-1', {active: activeTab == 'inbound', disabled: activeTab != 'inbound'})}
              >
                <ProductBreadcrumb
                  active={activeTab === 'inbound'}
                  type="inbound"
                  travel={inbound}
                  products={products}
                />
              </NavLink>
            </NavItem>
          }
        </Nav>
        <TabContent className="p-1 p-md-3" activeTab={activeTab} style={{border: '1px solid #dee2e6', borderTop: '0'}}>
          <TabPane tabId="outbound">
            {outboundRoutes ? (
              <Departures
              type="outbound"
              routes={outboundRoutes}
              selected={outbound}
              setSelected={setOutbound}
              onConfirmProduct={onConfirmProduct}
              onSelectMenuForAll={onSelectMenuForAll}
            />
            ) : (
              <NoRoutesAlert/>
            )}
          </TabPane>
          {inboundRoutes &&
            <TabPane tabId="inbound">
              {inboundRoutes ? (
                <Departures
                  type="inbound"
                  routes={inboundRoutes}
                  selected={inbound}
                  setSelected={setInbound}
                  departureArrival={get(outbound, 'route.legs[0].arrival_station.arrival_timestamp')}
                  onConfirmProduct={onConfirmProduct}
                  onSelectMenuForAll={onSelectMenuForAll}
                />
              ) : (
                <NoRoutesAlert/>
              )}
            </TabPane>
          }
        </TabContent>
      </ModuleBody>
      <ModuleFooter {...props}/>
    </ModulePanel>
  )
}

export default IryoTrainModule
